import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import { useAuth } from "./auth";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const buildColumnVisibility = (columns) =>
  columns.reduce((acc, col) => {
    acc[col.id] = true;
    return acc;
  }, {});

const buildColumnWidths = (columns) =>
  columns.reduce((acc, col) => {
    acc[col.id] = col.minWidth || 120;
    return acc;
  }, {});

const ENQUIRY_COLUMNS = [
  { id: "srno", label: "Sr No", minWidth: 80 },
  { id: "verificationno", label: "Verification", minWidth: 140 },
  { id: "enquiryno", label: "Enquiry No", minWidth: 160 },
  { id: "dateofreg", label: "Date Of Reg", minWidth: 140 },
  { id: "receivingdate", label: "Receiving Date", minWidth: 140 },
  { id: "transferredfrom", label: "Transferred From", minWidth: 170 },
  { id: "complainantname", label: "Complainant Name", minWidth: 180 },
  { id: "amount", label: "Amount", minWidth: 120 },
  { id: "categorytype", label: "Category Type", minWidth: 160 },
  { id: "gistofallegation", label: "Gist Of Allegation", minWidth: 260, wrap: true },
  { id: "status", label: "Status", minWidth: 140 },
  { id: "priority", label: "Priority", minWidth: 140 },
  { id: "allegedpeople", label: "Alleged People", minWidth: 200 },
  { id: "accountholderlocation", label: "Account Holder Location", minWidth: 220 },
  { id: "cdrlocation", label: "CDR Location", minWidth: 180 },
  { id: "cashoutlocation", label: "CashOut Location", minWidth: 180 },
  { id: "summondetails", label: "Summon Details", minWidth: 260, wrap: true },
  { id: "allegeddetails", label: "Alleged Details", minWidth: 260, wrap: true },
  { id: "complainantvisitdates", label: "Complainant Visit Dates", minWidth: 240, wrap: true },
  { id: "actions", label: "Actions", minWidth: 160 },
];

const ENQUIRY_COLUMN_MAP = ENQUIRY_COLUMNS.reduce((acc, col) => {
  acc[col.id] = col;
  return acc;
}, {});

const normalizeKey = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const excelDateToISO = (value) => {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === "number") {
    const date = new Date(Math.round((value - 25569) * 86400 * 1000));
    return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString().slice(0, 10);
  }
  return "";
};

const parseDateWithError = (value) => {
  if (value === null || value === undefined || value === "") {
    return { iso: "", error: "" };
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return { iso: value.toISOString().slice(0, 10), error: "" };
  }
  if (typeof value === "number") {
    const date = new Date(Math.round((value - 25569) * 86400 * 1000));
    return Number.isNaN(date.getTime())
      ? { iso: "", error: "Invalid Excel date number" }
      : { iso: date.toISOString().slice(0, 10), error: "" };
  }
  const raw = String(value).trim();
  if (!raw) return { iso: "", error: "" };
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return { iso: raw, error: "" };

  const simpleMatch = raw.match(/^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{2,4})$/);
  if (simpleMatch) {
    let [, p1, p2, y] = simpleMatch;
    if (y.length === 2) y = `20${y}`;
    if (y.length === 3) y = `20${y}`;
    let d = p1;
    let m = p2;
    if (Number(p1) > 12) {
      d = p1;
      m = p2;
    } else if (Number(p2) > 12) {
      d = p2;
      m = p1;
    } else {
      // ambiguous; default to month/day/year
      d = p2;
      m = p1;
    }
    const dd = String(d).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    const iso = `${y}-${mm}-${dd}`;
    const check = new Date(iso);
    return Number.isNaN(check.getTime())
      ? { iso: "", error: "Invalid date value" }
      : { iso, error: "" };
  }

  return {
    iso: "",
    error: "Invalid date format. Use DD-MM-YYYY or YYYY-MM-DD.",
  };
};
const toInputDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const formatDisplayDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const formatDisplayDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

const statusOptions = [
  "record requested",
  "record received",
  "record analysed",
  "record awaited",
  "court order to be prepared",
  "court order record requested",
  "CFR to legal",
  "CFR to FIR",
  "CFR to close",
  "CFR to transferred",
  "Other",
];

const priorityOptions = ["High", "Medium", "Low"];

const getPriorityChipColor = (value) => {
  const v = String(value || "").toLowerCase();
  if (v === "high") return "error";
  if (v === "medium") return "warning";
  if (v === "low") return "success";
  return "default";
};

const getStatusChipColor = (value) => {
  const v = String(value || "").toLowerCase();
  if (v.includes("record requested") || v.includes("record awaited")) return "warning";
  if (v.includes("record received") || v.includes("record analysed")) return "info";
  if (v.includes("cfr") || v.includes("court order")) return "secondary";
  if (v.includes("close") || v.includes("transferred")) return "success";
  return "default";
};

const normalizePriority = (value) => {
  const v = String(value || "").trim().toLowerCase();
  if (!v) return "";
  if (v.includes("high")) return "High";
  if (v.includes("medium")) return "Medium";
  if (v.includes("low")) return "Low";
  return value;
};

export default function EnquiryCaseDetails() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { canEdit, canDelete } = useAuth();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const limit = 20;

  const [search, setSearch] = useState("");
  const location = useLocation();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyEnquiry, setHistoryEnquiry] = useState("");
  const [categoryType, setCategoryType] = useState("");
  const [status, setStatus] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [summonFilter, setSummonFilter] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");

  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("Add");
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [formData, setFormData] = useState({
    verificationNo: "",
    enquiryNo: "",
    dateOfReg: "",
    receivingDate: "",
    transferredFrom: "Self",
    complainantName: "",
    amount: "",
    categoryType: "",
    gistOfAllegation: "",
    status: "",
    statusOther: "",
    priority: "Low",
    allegedPeople: "",
    accountHolderLocation: "",
    cdrLocation: "",
    cashOutLocation: "",
  });
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingValues, setEditingValues] = useState(null);

  const [enquiryOptions, setEnquiryOptions] = useState([]);

  const [visitDialogOpen, setVisitDialogOpen] = useState(false);
  const [visitManualEnquiry, setVisitManualEnquiry] = useState(true);
  const [visitForm, setVisitForm] = useState({
    enquiryCaseNum: "",
    personType: "Complainant",
    personName: "",
    visitDate: "",
    nextVisitDate: "",
    notes: "",
  });
  const [selectedTableRow, setSelectedTableRow] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importErrorOpen, setImportErrorOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [bulkPriorityOpen, setBulkPriorityOpen] = useState(false);
  const [bulkPriority, setBulkPriority] = useState("Low");
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildColumnVisibility(ENQUIRY_COLUMNS)
  );
  const [columnWidths, setColumnWidths] = useState(() =>
    buildColumnWidths(ENQUIRY_COLUMNS)
  );

  const requiredErrors = {
    enquiryNo: !formData.enquiryNo.trim(),
    complainantName: !formData.complainantName.trim(),
    categoryType: !formData.categoryType.trim(),
  };
  const hasRequiredErrors = Object.values(requiredErrors).some(Boolean);

  const buildQueryParams = (override = {}) => {
    const params = new URLSearchParams();
    params.set("page", override.page ?? page);
    params.set("limit", override.limit ?? limit);
    if (override.search ?? search) params.set("search", override.search ?? search);
    if (override.categoryType ?? categoryType) params.set("categoryType", override.categoryType ?? categoryType);
    if (override.status ?? status) params.set("status", override.status ?? status);
    if (override.priority ?? priorityFilter) params.set("priority", override.priority ?? priorityFilter);
    if (override.summonFilter ?? summonFilter) params.set("summon", override.summonFilter ?? summonFilter);
    if (override.amountMin ?? amountMin) params.set("amountMin", override.amountMin ?? amountMin);
    if (override.amountMax ?? amountMax) params.set("amountMax", override.amountMax ?? amountMax);
    return params;
  };

  const fetchRows = async () => {
    const params = buildQueryParams();
    const res = await fetch(`${API_BASE}/api/enquiries?${params.toString()}`);
    if (!res.ok) return;
    const json = await res.json();
    setRows(json.data || []);
    setTotalPages(Math.ceil((json.totalEntries || 0) / limit));
    setTotalEntries(Number(json.totalEntries || 0));
  };

  const fetchEnquiryOptions = async () => {
    const res = await fetch(`${API_BASE}/api/enquiries/list`);
    if (!res.ok) return;
    const json = await res.json();
    setEnquiryOptions(Array.isArray(json) ? json : []);
  };

  useEffect(() => {
    fetchRows();
  }, [page, search, categoryType, status, priorityFilter, summonFilter, amountMin, amountMax]);

  useEffect(() => {
    fetchEnquiryOptions();
  }, []);

  useEffect(() => {
    const incomingSearch = location.state?.search;
    if (incomingSearch) {
      setSearch(incomingSearch);
    }
  }, [location.state]);

  const openHistory = async (row) => {
    const enquiryNo = row?.enquiryno;
    if (!enquiryNo) return;
    setHistoryEnquiry(enquiryNo);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/change-history?enquiry=${encodeURIComponent(enquiryNo)}`
      );
      const data = res.ok ? await res.json() : [];
      setHistoryItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setHistoryItems([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenDialog = (type, row = null) => {
    if (!canEdit) {
      alert("Permission denied");
      return;
    }
    setDialogType(type);
    setSubmitAttempted(false);
    if (type === "Edit" && row) {
      setFormData({
        verificationNo: row.verificationno || "",
        enquiryNo: row.enquiryno || "",
        dateOfReg: toInputDate(row.dateofreg),
        receivingDate: toInputDate(row.receivingdate),
        transferredFrom: row.transferredfrom || "",
        complainantName: row.nameofcomplainant || "",
        amount: row.amount || "",
        categoryType: row.categorytype || "",
        gistOfAllegation: row.gistofallegation || "",
        status: statusOptions.includes(row.status) ? row.status : "Other",
        statusOther: statusOptions.includes(row.status) ? "" : row.status || "",
        priority: row.priority || "Low",
        allegedPeople: row.allegedpeople || "",
        accountHolderLocation: row.accountholderlocation || "",
        cdrLocation: row.cdrlocation || "",
        cashOutLocation: row.cashoutlocation || "",
      });
      setSelectedRow(row);
    } else {
      setFormData({
        verificationNo: "",
        enquiryNo: "",
        dateOfReg: "",
        receivingDate: "",
        transferredFrom: "Self",
        complainantName: "",
        amount: "",
        categoryType: "",
        gistOfAllegation: "",
        status: "",
        statusOther: "",
        priority: "Low",
        allegedPeople: "",
        accountHolderLocation: "",
        cdrLocation: "",
        cashOutLocation: "",
      });
      setSelectedRow(null);
    }
    setOpenDialog(true);
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const currentIds = rows.map((row) => row.id);
    const allSelected = currentIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : currentIds);
  };

  const startInlineEdit = (row) => {
    if (!canEdit) {
      alert("Permission denied");
      return;
    }
    const knownStatus = statusOptions.includes(row.status) ? row.status : "Other";
    setEditingRowId(row.id);
    setEditingValues({
      verificationNo: row.verificationno || "",
      enquiryNo: row.enquiryno || "",
      dateOfReg: toInputDate(row.dateofreg),
      receivingDate: toInputDate(row.receivingdate),
      transferredFrom: row.transferredfrom || "",
      complainantName: row.nameofcomplainant || "",
      amount: row.amount || "",
      categoryType: row.categorytype || "",
      gistOfAllegation: row.gistofallegation || "",
      status: knownStatus,
      statusOther: knownStatus === "Other" ? row.status || "" : "",
      priority: row.priority || "Low",
      allegedPeople: row.allegedpeople || "",
      accountHolderLocation: row.accountholderlocation || "",
      cdrLocation: row.cdrlocation || "",
      cashOutLocation: row.cashoutlocation || "",
    });
  };

  const cancelInlineEdit = () => {
    setEditingRowId(null);
    setEditingValues(null);
  };

  const saveInlineEdit = async () => {
    if (!canEdit) {
      alert("Permission denied");
      return;
    }
    if (!editingValues) return;
    if (
      !editingValues.enquiryNo.trim() ||
      !editingValues.complainantName.trim() ||
      !editingValues.categoryType.trim()
    ) {
      alert("Enquiry Case No, Complainant Name, and Category Type are required.");
      return;
    }
    const payload = {
      ...editingValues,
      status:
        editingValues.status === "Other"
          ? editingValues.statusOther
          : editingValues.status,
    };
    const res = await fetch(`${API_BASE}/api/enquiries/${editingRowId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      alert("Failed to update enquiry.");
      return;
    }
    cancelInlineEdit();
    fetchRows();
    fetchEnquiryOptions();
    window.dispatchEvent(new CustomEvent("enquiry-updated"));
  };

  const handleSubmit = async () => {
    if (!canEdit) {
      alert("Permission denied");
      return;
    }
    setSubmitAttempted(true);
    if (hasRequiredErrors) {
      alert("Enquiry Case No, Complainant Name, and Category Type are required.");
      return;
    }

    const payload = {
      ...formData,
      status: formData.status === "Other" ? formData.statusOther : formData.status,
    };

    const url =
      dialogType === "Add"
        ? `${API_BASE}/api/enquiries`
        : `${API_BASE}/api/enquiries/${selectedRow.id}`;
    const method = dialogType === "Add" ? "POST" : "PUT";

    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      alert("Failed to save enquiry.");
      return;
    }
    setOpenDialog(false);
    fetchRows();
    fetchEnquiryOptions();
    window.dispatchEvent(new CustomEvent("enquiry-updated"));
  };

  const handleDelete = async (row) => {
    if (!canDelete) {
      alert("Permission denied");
      return;
    }
    if (!window.confirm("Delete this enquiry?")) return;
    await fetch(`${API_BASE}/api/enquiries/${row.id}`, { method: "DELETE" });
    fetchRows();
    fetchEnquiryOptions();
    window.dispatchEvent(new CustomEvent("enquiry-updated"));
  };

  const handleBulkPrioritySave = async () => {
    if (!canEdit) {
      alert("Permission denied");
      return;
    }
    if (!selectedIds.length) {
      alert("Select at least one row.");
      return;
    }
    const res = await fetch(`${API_BASE}/api/enquiries/bulk-priority`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: selectedIds, priority: bulkPriority }),
    });
    if (!res.ok) {
      alert("Failed to update priority.");
      return;
    }
    setBulkPriorityOpen(false);
    setSelectedIds([]);
    fetchRows();
    window.dispatchEvent(new CustomEvent("enquiry-updated"));
  };

  const handleExport = async () => {
    setExporting(true);
    setExportProgress(0);
    try {
      const allRows = [];
      const limitFetch = 200;
      const firstParams = buildQueryParams({ page: 1, limit: limitFetch });
      const firstRes = await fetch(`${API_BASE}/api/enquiries?${firstParams.toString()}`);
      if (!firstRes.ok) throw new Error("Export failed");
      const firstJson = await firstRes.json();
      const totalEntries = Number(firstJson.totalEntries || 0);
      const totalPages = Math.max(1, Math.ceil(totalEntries / limitFetch));
      allRows.push(...(firstJson.data || []));
      setExportProgress(Math.round((1 / totalPages) * 100));

      for (let p = 2; p <= totalPages; p += 1) {
        const params = buildQueryParams({ page: p, limit: limitFetch });
        const res = await fetch(`${API_BASE}/api/enquiries?${params.toString()}`);
        if (!res.ok) throw new Error("Export failed");
        const json = await res.json();
        allRows.push(...(json.data || []));
        setExportProgress(Math.round((p / totalPages) * 100));
      }

      const exportRows = allRows.map((row, idx) => ({
        SerialNO: idx + 1,
        Verification: row.verificationno,
        EnquirNo: row.enquiryno,
        "DateOf reg": formatDisplayDate(row.dateofreg),
        ReceivingDate: formatDisplayDate(row.receivingdate),
        TransferredFrom: row.transferredfrom,
        NameOfComplainant: row.nameofcomplainant,
        Amount: row.amount,
        CategoryType: row.categorytype,
        GistOfAllegation: row.gistofallegation,
        Status: row.status,
        Priority: row.priority,
        AllegedPeople: row.allegedpeople,
        AccountHolderLocation: row.accountholderlocation,
        CDRLocation: row.cdrlocation,
        CashOutLocation: row.cashoutlocation,
        SummonDetails: row.summonDetails,
        "Complainant Visit dates": row.complainantVisitDates,
        AllegedDetails: row.allegedDetails,
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Enquiry Case Details");
      XLSX.writeFile(workbook, "EnquiryCaseDetails.xlsx");
    } catch (error) {
      console.error(error);
      alert("Export failed.");
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!canEdit) {
      alert("Permission denied");
      event.target.value = "";
      return;
    }
    setUploading(true);
    setUploadProgress(0);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
      const errors = [];

      const records = rawRows
        .map((row, idx) => {
          const normalized = {};
          Object.entries(row).forEach(([k, v]) => {
            normalized[normalizeKey(k)] = v;
          });
          const get = (...keys) => {
            for (const key of keys) {
              const val = normalized[normalizeKey(key)];
              if (val !== undefined && val !== null && String(val).trim() !== "") return val;
            }
            return "";
          };

          const dateOfRegRaw = get("dateofreg", "dateof reg");
          const receivingDateRaw = get("receivingdate");
          const dateOfRegParsed = parseDateWithError(dateOfRegRaw);
          const receivingDateParsed = parseDateWithError(receivingDateRaw);
          if (dateOfRegParsed.error) {
            errors.push({
              row: idx + 2,
              column: "DateOf reg",
              value: String(dateOfRegRaw),
              message: dateOfRegParsed.error,
            });
          }
          if (receivingDateParsed.error) {
            errors.push({
              row: idx + 2,
              column: "ReceivingDate",
              value: String(receivingDateRaw),
              message: receivingDateParsed.error,
            });
          }

          const rawPriority = normalizePriority(get("priority", "tier"));
          const record = {
            verificationNo: get("verification", "verificationno"),
            enquiryNo: get("enquirno", "enquiryno", "enquiry/caseno"),
            dateOfReg: dateOfRegParsed.iso,
            receivingDate: receivingDateParsed.iso,
            transferredFrom: get("transferredfrom"),
            complainantName: get("nameofcomplainant", "complainantname"),
            amount: get("amount"),
            categoryType: get("categorytype"),
            gistOfAllegation: get("gistofallegation"),
            status: get("status"),
            priority: rawPriority || "Low",
            allegedPeople: get("allegedpeople"),
            accountHolderLocation: get("accountholderlocation"),
            cdrLocation: get("cdrlocation"),
            cashOutLocation: get("cashoutlocation"),
          };
          return record;
        })
        .filter(
          (r) =>
            r.enquiryNo &&
            r.complainantName &&
            r.categoryType
        );
      const skipped = rawRows.length - records.length;

      if (errors.length) {
        setImportErrors(errors.slice(0, 50));
        setImportErrorOpen(true);
        return;
      }

      if (!records.length) {
        alert("No valid rows found.");
        return;
      }

      const chunkSize = 200;
      for (let i = 0; i < records.length; i += chunkSize) {
        const chunk = records.slice(i, i + chunkSize);
        const res = await fetch(`${API_BASE}/api/enquiries/bulk-upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ data: chunk }),
        });
        if (!res.ok) throw new Error("Upload failed");
        setUploadProgress(Math.round(((i + chunk.length) / records.length) * 100));
      }
      alert(`Upload complete. Imported ${records.length} rows.`);
      if (skipped > 0) {
        alert(`${skipped} rows were skipped because Enquiry Case No, Complainant Name, or Category Type was missing.`);
      }
      fetchRows();
      fetchEnquiryOptions();
      window.dispatchEvent(new CustomEvent("enquiry-updated"));
    } catch (error) {
      console.error(error);
      alert("Import failed.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      event.target.value = "";
    }
  };

  const handleVisitSubmit = async () => {
    if (!visitForm.enquiryCaseNum || !visitForm.personName || !visitForm.visitDate) {
      alert("Enquiry, person name, and visit date are required.");
      return;
    }

    const res = await fetch(`${API_BASE}/api/visit-logs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        enquiryCaseNum: visitForm.enquiryCaseNum,
        personType: visitForm.personType,
        personName: visitForm.personName,
        visitDate: visitForm.visitDate,
        nextVisitDate: visitForm.nextVisitDate,
        notes: visitForm.notes,
      }),
    });
    if (!res.ok) {
      alert("Failed to save visit log.");
      return;
    }
    window.dispatchEvent(new CustomEvent("visit-log-created"));
    setVisitDialogOpen(false);
    setVisitForm({
      enquiryCaseNum: "",
      personType: "Complainant",
      personName: "",
      visitDate: "",
      nextVisitDate: "",
      notes: "",
    });
    fetchRows();
  };

  const handleVisitEnquiryChange = async (value) => {
    setVisitForm((prev) => ({ ...prev, enquiryCaseNum: value }));
    if (!value) return;
    if (visitForm.personType === "Complainant") {
      const res = await fetch(`${API_BASE}/api/enquiries/lookup?case=${encodeURIComponent(value)}`);
      if (res.ok) {
        const data = await res.json();
        setVisitForm((prev) => ({
          ...prev,
          personName: data.nameofcomplainant || prev.personName,
        }));
      }
    }
  };

  const visibleEnquiryColumns = ENQUIRY_COLUMNS.filter(
    (col) => columnVisibility[col.id] !== false
  );
  const selectionColumnWidth = 48;
  const firstVisibleEnquiryColumn = visibleEnquiryColumns[0]?.id;
  const mobileEnquiryColumns = visibleEnquiryColumns.filter(
    (col) => col.id !== "srno" && col.id !== "actions" && col.id !== "status"
  );
  const currentRowIds = rows.map((row) => row.id);
  const selectedOnPage = currentRowIds.filter((id) => selectedIds.includes(id));
  const allSelected = currentRowIds.length > 0 && selectedOnPage.length === currentRowIds.length;
  const someSelected = selectedOnPage.length > 0 && !allSelected;

  const renderEnquiryValue = (colId, row) => {
    switch (colId) {
      case "complainantname":
        return row.nameofcomplainant || row.complainantname || "—";
      case "dateofreg":
        return formatDisplayDate(row.dateofreg);
      case "receivingdate":
        return formatDisplayDate(row.receivingdate);
      case "summondetails":
        return row.summonDetails || "—";
      case "allegeddetails":
        return row.allegedDetails || "—";
      case "complainantvisitdates":
        return row.complainantVisitDates || "—";
      case "priority":
        return row.priority ? (
          <Chip size="small" label={row.priority} color={getPriorityChipColor(row.priority)} />
        ) : (
          "—"
        );
      case "status":
        return row.status ? (
          <Chip size="small" label={row.status} color={getStatusChipColor(row.status)} />
        ) : (
          "—"
        );
      default:
        return row[colId] || "—";
    }
  };

  const getEnquiryCellSx = (colId, { isHeader = false, isSelected = false } = {}) => {
    const def = ENQUIRY_COLUMN_MAP[colId] || {};
    const width = columnWidths[colId] || def.minWidth || 120;
    const sx = {
      minWidth: width,
      width,
      whiteSpace: def.wrap ? "normal" : "nowrap",
    };
    if (colId === firstVisibleEnquiryColumn) {
      sx.position = "sticky";
      sx.left = selectionColumnWidth;
      sx.zIndex = isHeader ? 3 : 1;
      sx.backgroundColor = isHeader
        ? "background.paper"
        : isSelected
          ? "action.selected"
          : "background.paper";
    }
    return sx;
  };

  const getSelectCellSx = ({ isHeader = false, isSelected = false } = {}) => ({
    width: selectionColumnWidth,
    minWidth: selectionColumnWidth,
    position: "sticky",
    left: 0,
    zIndex: isHeader ? 4 : 2,
    backgroundColor: isHeader
      ? "background.paper"
      : isSelected
        ? "action.selected"
        : "background.paper",
  });

  const handleColumnVisibilityToggle = (id) => (event) => {
    const checked = event.target.checked;
    setColumnVisibility((prev) => {
      const next = { ...prev, [id]: checked };
      const visibleCount = Object.values(next).filter(Boolean).length;
      return visibleCount === 0 ? prev : next;
    });
  };

  const handleColumnWidthChange = (id) => (event) => {
    const def = ENQUIRY_COLUMN_MAP[id] || {};
    const fallback = def.minWidth || 120;
    const parsed = Number(event.target.value);
    const width = Number.isFinite(parsed) && parsed >= 60 ? parsed : fallback;
    setColumnWidths((prev) => ({ ...prev, [id]: width }));
  };

  const resetColumns = () => {
    setColumnVisibility(buildColumnVisibility(ENQUIRY_COLUMNS));
    setColumnWidths(buildColumnWidths(ENQUIRY_COLUMNS));
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, width: "100%", minWidth: 0 }}
    >
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between" sx={{ width: "100%" }}>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Button variant="contained" onClick={() => handleOpenDialog("Add")} disabled={!canEdit}>
            Add Enquiry
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              if (!selectedTableRow) {
                alert("Select a row first.");
                return;
              }
              handleOpenDialog("Edit", selectedTableRow);
            }}
            disabled={!canEdit}
          >
            Edit Selected
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              if (!selectedIds.length) {
                alert("Select at least one row.");
                return;
              }
              setBulkPriorityOpen(true);
            }}
            disabled={!canEdit}
          >
            Bulk Edit Priority
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setVisitDialogOpen(true)}
            disabled={!canEdit}
          >
            Add Visit Log
          </Button>
        </Stack>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Button variant="outlined" onClick={() => setColumnsOpen(true)}>
            Columns
          </Button>
          <Button variant="contained" onClick={handleExport} disabled={exporting}>
            Export All
          </Button>
          <Button variant="contained" color="secondary" component="label" disabled={uploading || !canEdit}>
            Upload Data
            <input type="file" hidden onChange={handleImport} />
          </Button>
        </Stack>
      </Stack>

      {(exporting || uploading) && (
        <Box sx={{ mt: 2 }}>
          {exporting && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2">Exporting... {exportProgress}%</Typography>
              <LinearProgress variant="determinate" value={exportProgress} />
            </Box>
          )}
          {uploading && (
            <Box>
              <Typography variant="body2">Uploading... {uploadProgress}%</Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
        </Box>
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2, width: "100%" }}>
        <TextField
          label="Search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          sx={{ width: { xs: "100%", md: 180 } }}
        />
        <TextField
          label="Category Type"
          value={categoryType}
          onChange={(e) => {
            setCategoryType(e.target.value);
            setPage(1);
          }}
        />
        <FormControl sx={{ minWidth: { xs: "100%", md: 200 } }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">All</MenuItem>
            {statusOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: { xs: "100%", md: 160 } }}>
          <InputLabel>Priority</InputLabel>
          <Select
            label="Priority"
            value={priorityFilter}
            onChange={(e) => {
              setPriorityFilter(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">All</MenuItem>
            {priorityOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: { xs: "100%", md: 160 } }}>
          <InputLabel>Summon</InputLabel>
          <Select
            label="Summon"
            value={summonFilter}
            onChange={(e) => {
              setSummonFilter(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="none">None</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Amount Min"
          type="number"
          value={amountMin}
          onChange={(e) => {
            setAmountMin(e.target.value);
            setPage(1);
          }}
          sx={{ width: { xs: "100%", md: 140 } }}
        />
        <TextField
          label="Amount Max"
          type="number"
          value={amountMax}
          onChange={(e) => {
            setAmountMax(e.target.value);
            setPage(1);
          }}
          sx={{ width: { xs: "100%", md: 140 } }}
        />
      </Stack>

      {isMobile ? (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {rows.length ? (
            rows.map((row) => {
              const isSelected = selectedTableRow?.id === row.id;
              const isChecked = selectedIds.includes(row.id);
              return (
                <Card
                  key={row.id}
                  variant="outlined"
                  onClick={() => setSelectedTableRow(row)}
                  sx={{
                    cursor: "pointer",
                    borderColor: isSelected ? "primary.main" : "divider",
                    bgcolor: isSelected ? "action.selected" : "background.paper",
                  }}
                >
                  <CardHeader
                    title={row.enquiryno || "Enquiry"}
                    subheader={row.nameofcomplainant || row.complainantname || "—"}
                    action={
                      <Stack direction="row" spacing={1} alignItems="center">
                        {row.status ? (
                          <Chip
                            size="small"
                            label={row.status}
                            color={getStatusChipColor(row.status)}
                          />
                        ) : null}
                        <Checkbox
                          checked={isChecked}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => toggleSelectRow(row.id)}
                          inputProps={{ "aria-label": "Select enquiry row" }}
                        />
                      </Stack>
                    }
                  />
                  <Divider />
                  <CardContent>
                    <Stack spacing={1.5}>
                      {mobileEnquiryColumns.map((col) => (
                        <Box key={col.id}>
                          <Typography variant="caption" color="text.secondary">
                            {col.label}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {renderEnquiryValue(col.id, row)}
                          </Typography>
                        </Box>
                      ))}
                      <Divider />
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button size="small" onClick={() => handleOpenDialog("Edit", row)} disabled={!canEdit}>
                          Edit
                        </Button>
                        <Button size="small" color="secondary" onClick={() => handleDelete(row)} disabled={!canDelete}>
                          Delete
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2">No data available</Typography>
            </Paper>
          )}
        </Stack>
      ) : (
        <Box sx={{ width: "100%", minWidth: 0 }}>
          <TableContainer
            component={Paper}
            sx={{
              mt: 2,
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflowX: "auto",
              overflowY: "auto",
              maxHeight: { xs: 360, sm: 460, md: 560 },
            }}
          >
            <Table stickyHeader size="small" sx={{ width: "100%", minWidth: 1800 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={getSelectCellSx({ isHeader: true })}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onClick={(event) => event.stopPropagation()}
                      onChange={toggleSelectAll}
                      inputProps={{ "aria-label": "Select all enquiries" }}
                    />
                  </TableCell>
                  {ENQUIRY_COLUMNS.filter((col) => columnVisibility[col.id] !== false).map(
                    (col) => (
                      <TableCell
                        key={col.id}
                        sx={getEnquiryCellSx(col.id, { isHeader: true })}
                      >
                        {col.label}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length ? (
                  rows.map((row, idx) => {
                    const isEditing = editingRowId === row.id;
                    const edit = editingValues || {};
                    const isSelected = selectedTableRow?.id === row.id;
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        selected={isSelected}
                        onClick={() => setSelectedTableRow(row)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell padding="checkbox" sx={getSelectCellSx({ isSelected })}>
                          <Checkbox
                            checked={selectedIds.includes(row.id)}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => toggleSelectRow(row.id)}
                            inputProps={{ "aria-label": "Select enquiry row" }}
                          />
                        </TableCell>
                        {columnVisibility.srno !== false && (
                          <TableCell sx={getEnquiryCellSx("srno", { isSelected })}>
                            {totalEntries
                              ? totalEntries - ((page - 1) * limit + idx)
                              : (page - 1) * limit + idx + 1}
                          </TableCell>
                        )}
                        {columnVisibility.verificationno !== false && (
                          <TableCell sx={getEnquiryCellSx("verificationno", { isSelected })}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={edit.verificationNo || ""}
                                onChange={(e) =>
                                  setEditingValues({ ...edit, verificationNo: e.target.value })
                                }
                              />
                            ) : (
                              row.verificationno
                            )}
                          </TableCell>
                        )}
                        {columnVisibility.enquiryno !== false && (
                          <TableCell sx={getEnquiryCellSx("enquiryno", { isSelected })}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={edit.enquiryNo || ""}
                                onChange={(e) =>
                                  setEditingValues({ ...edit, enquiryNo: e.target.value })
                                }
                                required
                              />
                            ) : (
                              row.enquiryno
                            )}
                          </TableCell>
                        )}
                        {columnVisibility.dateofreg !== false && (
                          <TableCell sx={getEnquiryCellSx("dateofreg", { isSelected })}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="date"
                                value={edit.dateOfReg || ""}
                                onChange={(e) =>
                                  setEditingValues({ ...edit, dateOfReg: e.target.value })
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            ) : (
                              formatDisplayDate(row.dateofreg)
                            )}
                          </TableCell>
                        )}
                        {columnVisibility.receivingdate !== false && (
                          <TableCell sx={getEnquiryCellSx("receivingdate", { isSelected })}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="date"
                                value={edit.receivingDate || ""}
                                onChange={(e) =>
                                  setEditingValues({ ...edit, receivingDate: e.target.value })
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            ) : (
                              formatDisplayDate(row.receivingdate)
                            )}
                          </TableCell>
                        )}
                        {columnVisibility.transferredfrom !== false && (
                          <TableCell sx={getEnquiryCellSx("transferredfrom", { isSelected })}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={edit.transferredFrom || ""}
                                onChange={(e) =>
                                  setEditingValues({ ...edit, transferredFrom: e.target.value })
                                }
                              />
                            ) : (
                              row.transferredfrom
                            )}
                          </TableCell>
                        )}
                        {columnVisibility.complainantname !== false && (
                          <TableCell sx={getEnquiryCellSx("complainantname", { isSelected })}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={edit.complainantName || ""}
                                onChange={(e) =>
                                  setEditingValues({ ...edit, complainantName: e.target.value })
                                }
                                required
                              />
                            ) : (
                              row.nameofcomplainant
                            )}
                          </TableCell>
                        )}
                        {columnVisibility.amount !== false && (
                          <TableCell sx={getEnquiryCellSx("amount", { isSelected })}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={edit.amount || ""}
                                onChange={(e) =>
                                  setEditingValues({ ...edit, amount: e.target.value })
                                }
                              />
                            ) : (
                              row.amount
                            )}
                          </TableCell>
                        )}
                        {columnVisibility.categorytype !== false && (
                          <TableCell sx={getEnquiryCellSx("categorytype", { isSelected })}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={edit.categoryType || ""}
                                onChange={(e) =>
                                  setEditingValues({ ...edit, categoryType: e.target.value })
                                }
                                required
                              />
                            ) : (
                              row.categorytype
                            )}
                          </TableCell>
                        )}
                        {columnVisibility.gistofallegation !== false && (
                          <TableCell sx={getEnquiryCellSx("gistofallegation", { isSelected })}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={edit.gistOfAllegation || ""}
                                onChange={(e) =>
                                  setEditingValues({ ...edit, gistOfAllegation: e.target.value })
                                }
                                multiline
                                minRows={2}
                              />
                            ) : (
                              row.gistofallegation
                            )}
                          </TableCell>
                        )}
                    {columnVisibility.status !== false && (
                      <TableCell sx={getEnquiryCellSx("status", { isSelected })}>
                        {isEditing ? (
                          <Stack spacing={1}>
                            <Select
                              size="small"
                              value={edit.status || ""}
                              onChange={(e) =>
                                setEditingValues({ ...edit, status: e.target.value })
                              }
                            >
                              {statusOptions.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                  {opt}
                                </MenuItem>
                              ))}
                            </Select>
                            {edit.status === "Other" && (
                              <TextField
                                size="small"
                                value={edit.statusOther || ""}
                                onChange={(e) =>
                                  setEditingValues({ ...edit, statusOther: e.target.value })
                                }
                              />
                            )}
                          </Stack>
                        ) : (
                          <Chip
                            label={row.status || "—"}
                            size="small"
                            color={getStatusChipColor(row.status)}
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.priority !== false && (
                      <TableCell sx={getEnquiryCellSx("priority", { isSelected })}>
                        {isEditing ? (
                          <Select
                            size="small"
                            value={edit.priority || ""}
                            onChange={(e) =>
                              setEditingValues({ ...edit, priority: e.target.value })
                            }
                          >
                            {priorityOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : (
                          <Chip
                            label={row.priority || "—"}
                            size="small"
                            color={getPriorityChipColor(row.priority)}
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    )}
                        {columnVisibility.allegedpeople !== false && (
                          <TableCell sx={getEnquiryCellSx("allegedpeople", { isSelected })}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={edit.allegedPeople || ""}
                                onChange={(e) =>
                                  setEditingValues({ ...edit, allegedPeople: e.target.value })
                                }
                                multiline
                                minRows={2}
                              />
                            ) : (
                              row.allegedpeople
                            )}
                          </TableCell>
                        )}
                        {columnVisibility.accountholderlocation !== false && (
                          <TableCell
                            sx={getEnquiryCellSx("accountholderlocation", { isSelected })}
                          >
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={edit.accountHolderLocation || ""}
                                onChange={(e) =>
                                  setEditingValues({
                                    ...edit,
                                    accountHolderLocation: e.target.value,
                                  })
                                }
                              />
                            ) : (
                              row.accountholderlocation
                            )}
                          </TableCell>
                        )}
                        {columnVisibility.cdrlocation !== false && (
                          <TableCell sx={getEnquiryCellSx("cdrlocation", { isSelected })}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={edit.cdrLocation || ""}
                                onChange={(e) =>
                                  setEditingValues({ ...edit, cdrLocation: e.target.value })
                                }
                              />
                            ) : (
                              row.cdrlocation
                            )}
                          </TableCell>
                        )}
                        {columnVisibility.cashoutlocation !== false && (
                          <TableCell sx={getEnquiryCellSx("cashoutlocation", { isSelected })}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={edit.cashOutLocation || ""}
                                onChange={(e) =>
                                  setEditingValues({ ...edit, cashOutLocation: e.target.value })
                                }
                              />
                            ) : (
                              row.cashoutlocation
                            )}
                          </TableCell>
                        )}
                        {columnVisibility.summondetails !== false && (
                          <TableCell sx={getEnquiryCellSx("summondetails", { isSelected })}>
                            {row.summonDetails}
                          </TableCell>
                        )}
                        {columnVisibility.allegeddetails !== false && (
                          <TableCell sx={getEnquiryCellSx("allegeddetails", { isSelected })}>
                            {row.allegedDetails}
                          </TableCell>
                        )}
                        {columnVisibility.complainantvisitdates !== false && (
                          <TableCell
                            sx={getEnquiryCellSx("complainantvisitdates", { isSelected })}
                          >
                            {row.complainantVisitDates}
                          </TableCell>
                        )}
                        {columnVisibility.actions !== false && (
                          <TableCell sx={getEnquiryCellSx("actions", { isSelected })}>
                            {isEditing ? (
                              <Stack direction="row" spacing={1}>
                                <Button onClick={saveInlineEdit} disabled={!canEdit}>Save</Button>
                                <Button color="secondary" onClick={cancelInlineEdit}>
                                  Cancel
                                </Button>
                              </Stack>
                            ) : (
                              <Stack direction="row" spacing={1}>
                                <Button onClick={() => startInlineEdit(row)} disabled={!canEdit}>
                                  Edit
                                </Button>
                                <Button onClick={() => openHistory(row)}>History</Button>
                                <Button color="secondary" onClick={() => handleDelete(row)} disabled={!canDelete}>
                                  Delete
                                </Button>
                              </Stack>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={Math.max(visibleEnquiryColumns.length + 1, 1)}>
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Pagination
        count={totalPages}
        page={page}
        onChange={(_, value) => setPage(value)}
        color="primary"
        size={isMobile ? "small" : "medium"}
        sx={{ mt: 2, display: "flex", justifyContent: "center" }}
      />

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Change History {historyEnquiry ? `• ${historyEnquiry}` : ""}</DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <LinearProgress />
          ) : historyItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No history entries found.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{formatDisplayDateTime(item.changed_at)}</TableCell>
                      <TableCell>{item.entity}</TableCell>
                      <TableCell>{item.action}</TableCell>
                      <TableCell>{item.changed_by || "system"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={columnsOpen} onClose={() => setColumnsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enquiry Table Columns</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {ENQUIRY_COLUMNS.map((col) => (
              <Stack
                key={col.id}
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={columnVisibility[col.id] !== false}
                      onChange={handleColumnVisibilityToggle(col.id)}
                    />
                  }
                  label={col.label}
                />
                <TextField
                  label="Width"
                  size="small"
                  type="number"
                  value={columnWidths[col.id] || col.minWidth || 120}
                  onChange={handleColumnWidthChange(col.id)}
                  inputProps={{ min: 60, max: 600 }}
                  sx={{ width: 140 }}
                />
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetColumns}>Reset</Button>
          <Button variant="contained" onClick={() => setColumnsOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkPriorityOpen}
        onClose={() => setBulkPriorityOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Bulk Edit Priority</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              label="Priority"
              value={bulkPriority}
              onChange={(e) => setBulkPriority(e.target.value)}
            >
              {priorityOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkPriorityOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleBulkPrioritySave} disabled={!canEdit}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{dialogType} Enquiry Case</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Required: Enquiry Case No, Complainant Name, Category Type
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Verification No"
              value={formData.verificationNo}
              onChange={(e) => setFormData({ ...formData, verificationNo: e.target.value })}
            />
            <TextField
              label="Enquiry Case No *"
              value={formData.enquiryNo}
              onChange={(e) => setFormData({ ...formData, enquiryNo: e.target.value })}
              error={submitAttempted && requiredErrors.enquiryNo}
              helperText={submitAttempted && requiredErrors.enquiryNo ? "Required" : ""}
            />
            <TextField
              label="Date Of Reg"
              type="date"
              value={formData.dateOfReg}
              onChange={(e) => setFormData({ ...formData, dateOfReg: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Receiving Date"
              type="date"
              value={formData.receivingDate}
              onChange={(e) => setFormData({ ...formData, receivingDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Transferred From"
              value={formData.transferredFrom}
              onChange={(e) => setFormData({ ...formData, transferredFrom: e.target.value })}
              placeholder="Self"
            />
            <TextField
              label="Complainant Name *"
              value={formData.complainantName}
              onChange={(e) => setFormData({ ...formData, complainantName: e.target.value })}
              error={submitAttempted && requiredErrors.complainantName}
              helperText={submitAttempted && requiredErrors.complainantName ? "Required" : ""}
            />
            <TextField
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            />
            <TextField
              label="Category Type *"
              value={formData.categoryType}
              onChange={(e) => setFormData({ ...formData, categoryType: e.target.value })}
              error={submitAttempted && requiredErrors.categoryType}
              helperText={submitAttempted && requiredErrors.categoryType ? "Required" : ""}
            />
            <TextField
              label="Gist Of Allegation"
              value={formData.gistOfAllegation}
              onChange={(e) => setFormData({ ...formData, gistOfAllegation: e.target.value })}
              multiline
              minRows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                {statusOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                label="Priority"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                {priorityOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formData.status === "Other" && (
              <TextField
                label="Status (Other)"
                value={formData.statusOther}
                onChange={(e) => setFormData({ ...formData, statusOther: e.target.value })}
              />
            )}
            <TextField
              label="Alleged People"
              value={formData.allegedPeople}
              onChange={(e) => setFormData({ ...formData, allegedPeople: e.target.value })}
              multiline
              minRows={2}
            />
            <TextField
              label="Account Holder Location"
              value={formData.accountHolderLocation}
              onChange={(e) => setFormData({ ...formData, accountHolderLocation: e.target.value })}
            />
            <TextField
              label="CDR Location"
              value={formData.cdrLocation}
              onChange={(e) => setFormData({ ...formData, cdrLocation: e.target.value })}
            />
            <TextField
              label="CashOut Location"
              value={formData.cashOutLocation}
              onChange={(e) => setFormData({ ...formData, cashOutLocation: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" disabled={!canEdit}>
            {dialogType}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={visitDialogOpen} onClose={() => setVisitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Visit Log</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Enquiry Case Number *</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={visitManualEnquiry}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (!checked && enquiryOptions.length === 0) {
                        alert("No enquiry list available. Manual entry will remain enabled.");
                        return;
                      }
                      setVisitManualEnquiry(checked);
                    }}
                  />
                }
                label="Manual entry"
              />
            </Stack>
            {visitManualEnquiry ? (
              <TextField
                label="Enquiry Case Number *"
                value={visitForm.enquiryCaseNum}
                onChange={(e) => handleVisitEnquiryChange(e.target.value)}
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel>Enquiry Case Number *</InputLabel>
                <Select
                  label="Enquiry Case Number *"
                  value={visitForm.enquiryCaseNum}
                  onChange={(e) => handleVisitEnquiryChange(e.target.value)}
                >
                  {enquiryOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel>Person Type</InputLabel>
              <Select
                label="Person Type"
                value={visitForm.personType}
                onChange={(e) =>
                  setVisitForm((prev) => ({ ...prev, personType: e.target.value }))
                }
              >
                <MenuItem value="Complainant">Complainant</MenuItem>
                <MenuItem value="Alleged">Alleged</MenuItem>
                <MenuItem value="Witness">Witness</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Person Name *"
              value={visitForm.personName}
              onChange={(e) =>
                setVisitForm((prev) => ({ ...prev, personName: e.target.value }))
              }
            />
            <TextField
              label="Visit Date *"
              type="date"
              value={visitForm.visitDate}
              onChange={(e) =>
                setVisitForm((prev) => ({ ...prev, visitDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Next Visit Date"
              type="date"
              value={visitForm.nextVisitDate}
              onChange={(e) =>
                setVisitForm((prev) => ({ ...prev, nextVisitDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Notes"
              value={visitForm.notes}
              onChange={(e) => setVisitForm((prev) => ({ ...prev, notes: e.target.value }))}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisitDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleVisitSubmit} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importErrorOpen} onClose={() => setImportErrorOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invalid Date Format</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Fix the dates in the Excel file and try again. Supported formats: DD-MM-YYYY or YYYY-MM-DD.
          </Typography>
          {importErrors.map((err, idx) => (
            <Typography key={`${err.row}-${idx}`} variant="body2">
              Row {err.row}, {err.column}: "{err.value}" — {err.message}
            </Typography>
          ))}
          {importErrors.length >= 50 && (
            <Typography variant="caption">Showing first 50 errors.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportErrorOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
